import DataPropertyProfileLinkManager from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLinkManager";
import {DataPropertyType} from "@/store/modules/hds/Model";
import {DataPropertyProfileLink} from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLink";

/**
 * DataPropertyProfileConnector のコンストラクタオプション
 */
interface ConstructorOptions {
  /**
   * 接続しているデータ項目プロファイルのサービスタイプ
   */
  serviceType: 'consume' | 'produce';

  /**
   * 接続しているデータ項目プロファイルの ID を取得する function
   */
  dataPropertyProfileId: () => string;

  /**
   * デフォルト値の初期値を取得する function
   */
  defaultValue: () => string | null;

  /**
   * 必須フラグを取得する function
   */
  isRequired: () => boolean;

  /**
   * データ型を取得する function
   */
  dataType: () => DataPropertyType;
}

/**
 * エラー
 */
class Errors {
  /** エラー: 利用側から値が取得できない */
  incompleteValueError = false;

  /** エラー: デフォルト値が必要 */
  needDefaultValueError = false;

  /** 警告: 異なるデータ型 */
  differentDataTypesWarn = false;

  /**
   * エラーが 1 つ以上ある場合 true そうでない場合 false
   */
  get hasError() {
    for(const key of Object.keys(this) as (keyof Errors)[])
      if(key.match(/Error$/) && this[key]) return true;
    return false;
  }

  /**
   * 警告が 1 つ以上ある場合 true そうでない場合 false
   */
  get hasWarn() {
    for(const key of Object.keys(this) as (keyof Errors)[])
      if(key.match(/Warn$/) && this[key]) return true;
    return false;
  }
}

/**
 * データ項目プロファイルコネクタ
 *
 * データ項目プロファイル を データ項目プロファイルリンクに接続する為のコネクタ
 */
export default class DataPropertyProfileConnector {
  /** コンストラクタオプション */
  private readonly options: ConstructorOptions;

  /**  DataPropertyProfileLinks*/
  private manager: DataPropertyProfileLinkManager | null = null;

  /** デフォルト値 */
  defaultValue: string | null;

  /**
   * コンストラクタ
   *
   * @param options コンストラクタオプション
   */
  constructor(options: ConstructorOptions) {
    this.options = options;
    this.defaultValue = options.defaultValue();
  }

  /**
   * 接続しているデータ項目プロファイルのサービスタイプを取得します
   */
  get serviceType() {
    return this.options.serviceType;
  }

  /**
   * 接続しているデータ項目プロファイルの ID
   */
  get dataPropertyProfileId() {
    return this.options.dataPropertyProfileId()
  }

  /**
   * 必須フラグ
   */
  get isRequired() {
    return this.options.isRequired();
  }

  /**
   * データ型
   */
  get dataType() {
    return this.options.dataType();
  }

  /**
   * このインスタンスを DataPropertyProfileLinks に登録します
   *
   * @param manager 登録先の DataPropertyProfileLinks
   */
  register(manager: DataPropertyProfileLinkManager): DataPropertyProfileConnector {
    manager.registerConnector(this);
    this.manager = manager;
    return this;
  }

  /**
   * このインスタンスを DataPropertyProfileLinks から登録解除します
   */
  unregister(): DataPropertyProfileConnector {
    if(this.manager !== null) {
      this.manager.unregisterConnector(this);
      this.manager = null;
    }
    return this;
  }

  /**
   * このコネクタに接続されているリンクをすべて取得します
   */
  get links(): DataPropertyProfileLink[] {
    if(this.manager === null) {
      return [];
    } else if(this.serviceType === 'consume') {
      return this.manager.get({consumerDataPropertyProfileId: this.dataPropertyProfileId});
    } else {
      return this.manager.get({producerDataPropertyProfileId: this.dataPropertyProfileId});
    }
  }

  /**
   * リンクを通じて接続されているコネクタをすべて取得します
   */
  get oppositeConnectors(): DataPropertyProfileConnector[] {
    if(this.manager === null) {
      return [];
    } else if(this.serviceType === 'consume') {
      return this.links
          .map(link => link.producerDataPropertyProfileId === null ? null : this.manager?.getConnector(link.producerDataPropertyProfileId))
          .filter(connector => !!connector) as DataPropertyProfileConnector[];
    } else {
      return this.links
          .map(link => link.consumerDataPropertyProfileId === null ? null : this.manager?.getConnector(link.consumerDataPropertyProfileId))
          .filter(connector => !!connector) as DataPropertyProfileConnector[];
    }
  }

  /**
   * エラーを取得します
   */
  errors() {
    const errors = new Errors();

    // 利用側のデータ項目プロファイルの場合
    if(this.serviceType === 'consume') {
      const connectors = this.oppositeConnectors;

      // 利用側に値が必要な場合に、提供側とのマッピングが存在するかどうか
      errors.incompleteValueError = (() => {
        // 必須ではない、もしくはデフォルト値が設定されている場合、値は取得可能
        if(!this.isRequired || this.defaultValue) {
          return false;
        }

        // 接続先の提供側のデータ項目プロファイルがある場合
        if(connectors.length > 0) {
          return false;
        }

        // マッピングが存在しない
        return true;
      })();

      // 利用側から値が取得できるかどうか
      errors.needDefaultValueError = !errors.incompleteValueError && (() => {
        // 必須ではない、もしくはデフォルト値が設定されている場合、値は取得可能
        if (!this.isRequired || this.defaultValue) {
          return false;
        }

        // 接続先の提供側のデータ項目プロファイルが必須、もしくはデフォルト値がある場合、値は取得可能
        for (const conn of connectors) {
          if (conn.isRequired || conn.defaultValue) {
            return false;
          }
        }

        // 値は取得不能
        return true;
      })();

      // 異なるデータ型かどうか
      errors.differentDataTypesWarn = (() => {
        for(const conn of connectors) {
          if(conn.dataType !== this.dataType) {
            return true;
          }
        }
        return false;
      })();
    }

    // 提供側のデータ項目プロファイルの場合
    if(this.serviceType === 'produce') {
      const connectors = this.oppositeConnectors;

      // 異なるデータ型かどうか
      errors.differentDataTypesWarn = (() => {
        for(const conn of connectors) {
          if(conn.dataType !== this.dataType) {
            return true;
          }
        }
        return false;
      })();
    }

    return errors as Readonly<Errors>;
  }
}
